import React, { useEffect, useState } from "react";
import { collection, getDocs, doc, getDoc } from "firebase/firestore";
import { db } from "../config/firebase";
import PageTemplate from "../templates/PageTemplate";
import { Card, CardHeader, CardContent, CardTitle } from "../components/Card";
import moment from "moment";
import { useNavigate } from "react-router-dom";

const STATUS_LABELS = {
  pending: "En attente",
  confirmed: "Confirmée",
  refused: "Refusée",
};

const STATUS_COLORS = {
  pending: "bg-yellow-100 text-yellow-800 border-yellow-200",
  confirmed: "bg-green-100 text-green-800 border-green-200",
  refused: "bg-red-100 text-red-800 border-red-200",
};

export default function OffersList() {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");
  const navigate = useNavigate();

  useEffect(() => {
    async function fetchOffers() {
      setLoading(true);
      const snap = await getDocs(collection(db, "offers"));
      const data = [];

      for (const docSnap of snap.docs) {
        const offer = docSnap.data();

        let authorEmail = "";
        if (offer.authorId) {
          const userSnap = await getDoc(doc(db, "users", offer.authorId));
          if (userSnap.exists()) {
            authorEmail = userSnap.data().email || "";
          }
        }

        data.push({
          id: docSnap.id,
          message: offer.message,
          price: offer.price,
          projectId: offer.projectId,
          status: offer.status,
          authorEmail,
          createdAt: offer.createdAt,
        });
      }

      // Trier par date de création (plus récent en premier)
      data.sort((a, b) => {
        const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0);
        const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0);
        return dateB - dateA;
      });

      setOffers(data);
      setLoading(false);
    }
    fetchOffers();
  }, []);

  const filteredOffers =
    filterStatus === "all"
      ? offers
      : offers.filter((o) => o.status === filterStatus);

  const statusCounts = {
    all: offers.length,
    pending: offers.filter((o) => o.status === "pending").length,
    confirmed: offers.filter((o) => o.status === "confirmed").length,
    refused: offers.filter((o) => o.status === "refused").length,
  };

  return (
    <PageTemplate
      loading={loading}
      content={
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Info Card */}
          <Card className="bg-gradient-to-r from-red-50 to-rose-50 border-red-200">
            <CardContent className="flex items-start gap-4">
              <div className="p-3 bg-[#801111] rounded-lg">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="w-6 h-6 text-white"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9 14.25 4.75 12m4.25 2.25 4.25-2.25M19.25 12 15 14.25m4.25-2.25a48.329 48.329 0 0 0-9-3.75m9 3.75a48.329 48.329 0 0 1-9 3.75m-9-3.75h.008v.008H4.992V12Zm9.5 0h.008v.008h-.008V12Zm9.504 0h.008v.008h-.008V12Z"
                  />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">
                  Gestion des offres
                </h3>
                <p className="text-sm text-gray-600">
                  Consultez toutes les offres soumises par les couturiers pour les
                  projets. {offers.length} offre{offers.length > 1 ? "s" : ""} au
                  total.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card
              className={`cursor-pointer transition-all hover:shadow-lg ${
                filterStatus === "all"
                  ? "ring-2 ring-[#801111] bg-red-50"
                  : "hover:bg-gray-50"
              }`}
              onClick={() => setFilterStatus("all")}
            >
              <CardContent className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">
                    Total des offres
                  </p>
                  <p className="text-2xl font-bold text-[#801111]">
                    {statusCounts.all}
                  </p>
                </div>
                <div className="p-3 bg-red-100 rounded-full">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="w-6 h-6 text-[#801111]"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 0 0 2.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 0 0-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25ZM6.75 12h.008v.008H6.75V12Zm0 3h.008v.008H6.75V15Zm0 3h.008v.008H6.75V18Z"
                    />
                  </svg>
                </div>
              </CardContent>
            </Card>

            <Card
              className={`cursor-pointer transition-all hover:shadow-lg ${
                filterStatus === "pending"
                  ? "ring-2 ring-yellow-500 bg-yellow-50"
                  : "hover:bg-gray-50"
              }`}
              onClick={() => setFilterStatus("pending")}
            >
              <CardContent className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">En attente</p>
                  <p className="text-2xl font-bold text-yellow-600">
                    {statusCounts.pending}
                  </p>
                </div>
                <div className="p-3 bg-yellow-100 rounded-full">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="w-6 h-6 text-yellow-600"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                    />
                  </svg>
                </div>
              </CardContent>
            </Card>

            <Card
              className={`cursor-pointer transition-all hover:shadow-lg ${
                filterStatus === "confirmed"
                  ? "ring-2 ring-green-500 bg-green-50"
                  : "hover:bg-gray-50"
              }`}
              onClick={() => setFilterStatus("confirmed")}
            >
              <CardContent className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Confirmées</p>
                  <p className="text-2xl font-bold text-green-600">
                    {statusCounts.confirmed}
                  </p>
                </div>
                <div className="p-3 bg-green-100 rounded-full">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="w-6 h-6 text-green-600"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                    />
                  </svg>
                </div>
              </CardContent>
            </Card>

            <Card
              className={`cursor-pointer transition-all hover:shadow-lg ${
                filterStatus === "refused"
                  ? "ring-2 ring-red-500 bg-red-50"
                  : "hover:bg-gray-50"
              }`}
              onClick={() => setFilterStatus("refused")}
            >
              <CardContent className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Refusées</p>
                  <p className="text-2xl font-bold text-red-600">
                    {statusCounts.refused}
                  </p>
                </div>
                <div className="p-3 bg-red-100 rounded-full">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="w-6 h-6 text-red-600"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M6 18 18 6M6 6l12 12"
                    />
                  </svg>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Offers Table */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>
                  {filterStatus === "all"
                    ? "Toutes les offres"
                    : `${STATUS_LABELS[filterStatus]} (${statusCounts[filterStatus]})`}
                </CardTitle>
                {filterStatus !== "all" && (
                  <button
                    onClick={() => setFilterStatus("all")}
                    className="text-sm text-[#801111] hover:text-[#5a0c0c] font-medium flex items-center gap-1"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      className="w-4 h-4"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M6 18 18 6M6 6l12 12"
                      />
                    </svg>
                    Réinitialiser le filtre
                  </button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {filteredOffers.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      className="w-8 h-8 text-gray-400"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 0 0 2.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 0 0-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25ZM6.75 12h.008v.008H6.75V12Zm0 3h.008v.008H6.75V15Zm0 3h.008v.008H6.75V18Z"
                      />
                    </svg>
                  </div>
                  <p className="text-gray-600 font-medium mb-1">
                    Aucune offre trouvée
                  </p>
                  <p className="text-sm text-gray-500">
                    {filterStatus === "all"
                      ? "Aucune offre n'a été créée pour le moment"
                      : `Aucune offre avec le statut "${STATUS_LABELS[filterStatus]}"`}
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead className="bg-gray-50 border-b-2 border-gray-200">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                          Auteur
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                          Message
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                          Prix
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                          Projet
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                          Statut
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                          Date
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                          Action
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredOffers.map((o) => (
                        <tr
                          key={o.id}
                          className="hover:bg-gray-50 transition-colors"
                        >
                          <td className="px-4 py-4">
                            {o.authorEmail || (
                              <span className="text-xs italic text-red-600 bg-red-50 px-2 py-1 rounded">
                                Inconnu
                              </span>
                            )}
                          </td>
                          <td
                            className="px-4 py-4 max-w-xs"
                            title={o.message}
                          >
                            <span className="text-sm text-gray-600 line-clamp-2">
                              {o.message.length > 50
                                ? o.message.slice(0, 50) + "…"
                                : o.message}
                            </span>
                          </td>
                          <td className="px-4 py-4">
                            <div className="flex items-center gap-1">
                              <span className="text-sm font-semibold text-[#801111]">
                                {o.price ?? "-"}
                              </span>
                              {o.price && (
                                <span className="text-xs text-gray-500">€</span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-4">
                            <span className="text-xs font-mono bg-gray-100 px-2 py-1 rounded">
                              {o.projectId
                                ? `${o.projectId.slice(0, 8)}...`
                                : "-"}
                            </span>
                          </td>
                          <td className="px-4 py-4">
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${
                                STATUS_COLORS[o.status] ||
                                "bg-gray-100 text-gray-800 border-gray-200"
                              }`}
                            >
                              {STATUS_LABELS[o.status] || o.status}
                            </span>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                            {o.createdAt && o.createdAt.toDate
                              ? moment(o.createdAt.toDate()).format(
                                  "DD/MM/YYYY HH:mm"
                                )
                              : o.createdAt
                              ? moment(o.createdAt).format("DD/MM/YYYY HH:mm")
                              : "-"}
                          </td>
                          <td className="px-4 py-4">
                            <button
                              onClick={() => navigate(`/offers/${o.id}`)}
                              className="inline-flex items-center gap-1 text-[#801111] hover:text-[#5a0c0c] font-medium text-sm transition-colors"
                              aria-label={`Voir détails de l'offre ${o.id}`}
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth={1.5}
                                stroke="currentColor"
                                className="w-4 h-4"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z"
                                />
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
                                />
                              </svg>
                              Voir
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      }
    />
  );
}